import { FormEvent, useState, useEffect } from 'react';
import type { MessageTemplate, MessageCategory } from '../../types/messageTemplate';
import type { Integration } from '../../types/integration';
import type { WhatsAppSession } from '../../types/assistant';
import { Form, FormField, FormLabel, FormInput, FormSelect, FormTextarea, Button } from '../ui';

interface TemplateFormProps {
  template: MessageTemplate | null;
  categories: MessageCategory[];
  integrations: Integration[];
  whatsappSessions: WhatsAppSession[];
  isGlobal: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onLoadDefault: () => void;
  onCategoryChange: (categoryKey: string) => void;
  isLoading: boolean;
  onClose: () => void;
}

function TemplateForm({
  template,
  categories,
  integrations,
  whatsappSessions,
  isGlobal,
  onSubmit,
  onLoadDefault,
  onCategoryChange,
  isLoading,
  onClose,
}: TemplateFormProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>(template?.category_key || '');

  useEffect(() => {
    if (template) {
      setSelectedCategory(template.category_key);
    }
  }, [template]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCategory = e.target.value;
    setSelectedCategory(newCategory);
    onCategoryChange(newCategory);
  };

  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="name" required>Nome</FormLabel>
        <FormInput
          id="name"
          name="name"
          type="text"
          defaultValue={template?.name || ''}
          placeholder="Nome do template"
          maxLength={100}
          required
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="category_key" required>
          Categoria
        </FormLabel>
        <FormSelect
          id="category_key"
          name="category_key"
          value={selectedCategory}
          onChange={handleCategoryChange}
          required
        >
          <option value="">Selecione uma categoria</option>
          {categories.map(category => (
            <option key={category.id} value={category.key}>
              {category.description}
            </option>
          ))}
        </FormSelect>
      </FormField>

      <FormField>
        <div className="flex justify-between items-center">
          <FormLabel htmlFor="message_template" required>
            Modelo de Mensagem
          </FormLabel>
          {!isGlobal && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onLoadDefault}
              disabled={!selectedCategory}
            >
              Carregar Modelo Padrão
            </Button>
          )}
        </div>
        <FormTextarea
          id="message_template"
          name="message_template"
          defaultValue={template?.message_template}
          required
          rows={10}
          placeholder="Digite o modelo de mensagem aqui..."
        />
        <p className="mt-2 text-sm text-gray-500">
          Você pode usar variáveis como {'{nome}'}, {'{data}'}, etc. que serão substituídas ao
          enviar a mensagem.
        </p>
      </FormField>

      <FormField>
        <FormLabel htmlFor="message_json">
          Dados JSON (Opcional)
        </FormLabel>
        <FormTextarea
          id="message_json"
          name="message_json"
          defaultValue={template?.message_json ? JSON.stringify(template.message_json, null, 2) : ''}
          rows={10}
          placeholder='{"campo1": "valor1", "campo2": "valor2"}'
        />
        <p className="mt-2 text-sm text-gray-500">
          JSON com dados estruturados da mensagem. Deve ser um objeto JSON válido.
        </p>
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="integration_id">Integração</FormLabel>
          <FormSelect
            id="integration_id"
            name="integration_id"
            defaultValue={template?.integration_id?.toString() || ''}
          >
            <option value="">Nenhuma (opcional)</option>
            {integrations.map(integration => (
              <option key={integration.id} value={integration.id}>
                {integration.description}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="whatsapp_id">Sessão do WhatsApp</FormLabel>
          <FormSelect
            id="whatsapp_id"
            name="whatsapp_id"
            defaultValue={template?.whatsapp_id?.toString() || ''}
          >
            <option value="">Nenhuma (opcional)</option>
            {whatsappSessions.map(session => (
              <option key={session.id} value={session.id}>
                {session.session_name}
              </option>
            ))}
          </FormSelect>
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="integration_key">Chave de Integração</FormLabel>
          <FormInput
            id="integration_key"
            name="integration_key"
            type="text"
            defaultValue={template?.integration_key || ''}
            placeholder="Chave para integração externa (opcional)"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="hsm_key">Chave HSM</FormLabel>
          <FormInput
            id="hsm_key"
            name="hsm_key"
            type="text"
            defaultValue={template?.hsm_key || ''}
            placeholder="Chave do template HSM (opcional)"
          />
        </FormField>
      </div>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {template ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default TemplateForm;
